import * as React from 'react';

type Props = {
  value: number;              // 0..5 (or any scale via outOf)
  count?: number | null;      // review count, optional
  outOf?: number;             // default 5
  size?: 'sm' | 'md' | 'lg';  // visual size
  showNumber?: boolean;       // show 4.7 text
  className?: string;
};

const SIZE_MAP = { sm: 'text-[12px]', md: 'text-[14px]', lg: 'text-[18px]' } as const;

export default function StarRating({
  value,
  count = null,
  outOf = 5,
  size = 'md',
  showNumber = true,
  className = '',
}: Props) {
  const v = Number.isFinite(value) ? Math.max(0, Math.min(value, outOf)) : 0;
  const pct = (v / outOf) * 100;
  const label = `${v.toFixed(1)} out of ${outOf}${typeof count === 'number' ? ` based on ${formatCount(count)} reviews` : ''}`;

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <span className={`relative leading-none ${SIZE_MAP[size]} select-none`} aria-hidden="true">
        <span className="text-gray-300">★★★★★</span>
        <span className="absolute inset-0 text-amber-500 overflow-hidden" style={{ width: `${pct}%` }}>
          ★★★★★
        </span>
      </span>

      {showNumber && (
        <span className="text-sm text-gray-900 tabular-nums">{v.toFixed(1)}</span>
      )}

      {typeof count === 'number' && (
        <span className="text-xs text-gray-500 tabular-nums">({formatCount(count)})</span>
      )}

      <span className="sr-only" aria-live="polite">{label}</span>
    </div>
  );
}

function formatCount(n: number) {
  try {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  } catch { return String(n); }
}
